'use client';
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import GlideDefault from '@glidejs/glide/dist/glide.esm.js';
import classnames from 'classnames';
import * as React from 'react';
import { createPortal } from 'react-dom';
import { Button } from '../../index.js';
import { VerticalImageGalleryDots } from './VerticalImageGalleryDots.js';
import { VerticalImageGalleryProvider, useVerticalImageGallery, } from './VerticalImageGalleryProvider.js';
const Glide = (_a = GlideDefault.default) !== null && _a !== void 0 ? _a : GlideDefault;
const VerticalImageGalleryMain = React.forwardRef((_a, ref) => {
    var { id, className, children, rewindDefault = false, startAtDefault = 0, perViewDefault = 1, lightbox, imageComponent, imageComponentProps, maxNumberOfItemsToDisplay, onLastDisplayedItemClick } = _a, other = __rest(_a, ["id", "className", "children", "rewindDefault", "startAtDefault", "perViewDefault", "lightbox", "imageComponent", "imageComponentProps", "maxNumberOfItemsToDisplay", "onLastDisplayedItemClick"]);
    const carouselRef = React.useRef(null);
    const buttonNext = React.useRef(null);
    const buttonPrev = React.useRef(null);
    const { setActiveTab } = useVerticalImageGallery() || {};
    const componentClassName = classnames('sparky-c-vertical-image-gallery-wrapper', className, {
        'sparky-c-vertical-image-gallery-wrapper--lightbox': lightbox,
    });
    const ArrowDisabler = function (Glide, Components) {
        return {
            mount() {
                if (!Glide.on) {
                    console.warn('Glide.on not found');
                    return;
                }
                Glide.on(['mount.after', 'run'], () => {
                    setActiveTab === null || setActiveTab === void 0 ? void 0 : setActiveTab(Glide.index);
                    if (buttonPrev.current) {
                        if (Glide.index === 0) {
                            buttonPrev.current.classList.add('glide__arrow--disabled');
                            buttonPrev.current.setAttribute('disabled', 'disabled');
                        }
                        else {
                            buttonPrev.current.classList.remove('glide__arrow--disabled');
                            buttonPrev.current.removeAttribute('disabled');
                        }
                    }
                    if (buttonNext) {
                        if (!Glide.settings)
                            return;
                        const componentsSize = Components.Sizes && typeof Components.Sizes.length === 'number'
                            ? Components.Sizes.length
                            : 0;
                        if (Glide.index === componentsSize - Glide.settings.perView) {
                            if (buttonNext.current) {
                                buttonNext.current.classList.add('glide__arrow--disabled');
                                buttonNext.current.setAttribute('disabled', 'disabled');
                            }
                        }
                        else {
                            if (buttonNext.current) {
                                buttonNext.current.classList.remove('glide__arrow--disabled');
                                buttonNext.current.removeAttribute('disabled');
                            }
                        }
                    }
                });
            },
        };
    };
    React.useImperativeHandle(ref, () => {
        if (!carouselRef.current)
            return document.createElement('div');
        return carouselRef.current;
    });
    React.useEffect(() => {
        const options = {
            startAt: startAtDefault,
            perView: perViewDefault,
            rewind: rewindDefault,
            bound: true,
            keyboard: false,
            center: true,
            perViewDefault: 1,
            perTouch: 1,
            focusAt: 'center',
        };
        if (carouselRef.current === null)
            return;
        const glide = new Glide(carouselRef.current, options);
        setTimeout(() => {
            const glideInstance = glide === null || glide === void 0 ? void 0 : glide.mount({
                ArrowDisabler: ArrowDisabler,
            });
            return () => {
                glideInstance.destroy();
            };
        }, 300);
    }, [children, perViewDefault, rewindDefault, startAtDefault]);
    return (_jsxs("div", Object.assign({ id: id, className: componentClassName, ref: carouselRef }, { children: [_jsx("div", Object.assign({ className: "sparky-c-vertical-image-gallery__inner sparky-u-margin-bottom-md" }, other, { "data-glide-el": "track" }, { children: _jsx("ol", Object.assign({ className: "sparky-c-vertical-image-gallery__list" }, { children: React.Children.toArray(children) })) })), _jsx("div", { id: "vertical-image-gallery-portal" }), lightbox && (_jsxs("div", Object.assign({ className: "sparky-c-vertical-image-gallery__controls", "data-glide-el": "controls" }, { children: [_jsx(Button, { circle: true, text: "arrow-prev", iconName: "chevron-left", iconPosition: "before", hideText: true, variant: "primary", className: "sparky-c-vertical-image-gallery__button sparky-c-vertical-image-gallery__button-prev", "data-glide-dir": "|<", buttonRef: buttonPrev }), _jsx(Button, { circle: true, text: "arrow-next", iconName: "chevron-right", iconPosition: "before", hideText: true, variant: "primary", className: "sparky-c-vertical-image-gallery__button sparky-c-vertical-image-gallery__button-next", "data-glide-dir": "|>", buttonRef: buttonNext })] }))), _jsx("div", Object.assign({ className: "glide__bullets sparky-c-vertical-image-gallery__bullets", "data-glide-el": "controls[nav]" }, { children: _jsx(VerticalImageGalleryDots, { childArray: React.Children.toArray(children), imageComponent: imageComponent, imageComponentProps: imageComponentProps, maxNumberOfItemsToDisplay: maxNumberOfItemsToDisplay, onLastDisplayedItemClick: onLastDisplayedItemClick }) }))] })));
});
export const VerticalImageGalleryPortal = ({ children }) => {
    const portalWrapper = document.getElementById('vertical-image-gallery-portal');
    if (!portalWrapper) {
        return _jsx(_Fragment, { children: children });
    }
    return createPortal(children, portalWrapper);
};
export const VerticalImageGallery = (props) => {
    return (_jsx(VerticalImageGalleryProvider, { children: _jsx(VerticalImageGalleryMain, Object.assign({}, props)) }));
};
VerticalImageGallery.displayName = 'VerticalImageGallery';
VerticalImageGalleryMain.displayName = 'VerticalImageGalleryMain';
//# sourceMappingURL=VerticalImageGallery.js.map